import Constants from "./constants";
import axios from "axios";

const { API_LINK } = Constants;

class Auth {
  constructor() {
    this.refreshToken = localStorage.getItem("refreshToken");
    this.accessToken = localStorage.getItem("accessToken");

    if (this.refreshToken && this.isTokenExpired(this.refreshToken)) {
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");

      this.refreshToken = null;
      this.accessToken = null;
    }

    this.authenticated = !!this.refreshToken;
  }

  isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      if (!payload.exp) return false;

      return Date.now() >= payload.exp * 1000;
    } catch (e) {
      return true;
    }
  }

  async fetch(url, options = {}) {
    if (!this.isAuthenticated()) {
      return Response.error();
    }

    if (!this.accessToken || this.isTokenExpired(this.accessToken)) {
      const response = await fetch("/api/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.ok) {
        const { data } = await response.json();
        this.accessToken = data.accessToken;
        localStorage.setItem("accessToken", this.accessToken);
      } else {
        this.logout();

        return Response.error();
      }
    }

    const injectedOptions = {
      ...options,
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        ...options.headers,
      },
    };

    return fetch(url, injectedOptions);
  }

  async axios(url, options = {}) {
    if (!this.isAuthenticated()) {
      throw new Error("User is not authenticated.");
    }

    if (!this.accessToken || this.isTokenExpired(this.accessToken)) {
      const response = await axios.post("/api/refresh", { refreshToken: this.refreshToken }, {
        headers: {
          "Content-Type": "application/json",
        }
      });

      if (response.status === 200) {
        const { data } = response;
        this.accessToken = data.accessToken;
        localStorage.setItem("accessToken", this.accessToken);
      } else {
        this.logout();
        throw new Error("Session expired. Please log in again.");
      }
    }

    const injectedOptions = {
      ...options,
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        ...options.headers,
      },
    };

    return axios.create(injectedOptions);
  }

  async login(username, password) {
    const rawStatus = await fetch("/api/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });
    try {
      const status = await rawStatus.json();
      // console.log("==== status", status);
      const { refreshToken, ...restData } = status.data || {};

      if (rawStatus.status === 200) {
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
          this.refreshToken = refreshToken;

          this.setAuthenticated(true);
        }

        if (this.dispatch) {
          this.dispatch({
            type: "LOGIN",
            payload: {
              id: restData.id,
              username: restData.username,
              userGroupId: restData.userGroupId,
              operatorId: restData.operator?.id,
              ...(restData.isTd ? { isTd: restData.isTd } : null),
              ...(restData.isMegaOp ? { isMegaOp: restData.isMegaOp } : null),
              permissionPage: restData.permissionPage,
              routePermissionId: restData.routePermissionId,
              pwExpiry: restData.pwExpiry,
            },
          });
        }

        return { ...restData, code: 200 };
      }

      return status;
    } catch (error) {
      console.error("Login error:", error);
      return { code: 500, message: "Login error" };
    }
  }

  setAuthenticated(val) {
    this.authenticated = val;
  }

  logout() {
    const cb = () => {
      this.authenticated = false;
      localStorage.clear();

      if (this.dispatch) {
        this.dispatch({ type: "LOGOUT" });
      } else {
        console.warn("No dispatch function set in Auth instance.");
      }
      window.location.reload();
    };
    if (this.refreshToken) {
      fetch("/api/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      })
        .catch((error) => {
          console.error("Logout error:", error);
        })
        .finally(() => cb());
    } else {
      cb();
    }
  }

  isAuthenticated() {
    return this.authenticated;
  }

  setDispatch(dispatch) {
    this.dispatch = dispatch;

    if (!this.isAuthenticated()) {
      this.dispatch({ type: "LOGOUT" });
    }
  }
}

export default new Auth();
